// --- Dependencies -------------------------------------------------------------------------------
import { useMemo } from "react";
import { NavLink } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyLogSeverity, TyOverviewLogs, TyOverviewMeta } from "@/types";
import Chart from "@/UI/atoms/Chart";
import SeverityBar from "@/UI/atoms/SeverityBar";
import StatTile from "@/UI/atoms/StatTile";
import { alignToBuckets, buildBuckets, formatBucket, formatMs, formatNumber, formatPercent } from "./helpers";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    data: TyOverviewLogs;
    meta: TyOverviewMeta;
};

// --- Logic --------------------------------------------------------------------------------------

const TONES: Record<TyLogSeverity, "good" | "warn" | "bad"> = { success: "good", rejected: "warn", error: "bad" };

/**
 * Two charts on one shared time axis rather than one stacked chart.
 *
 * Stacking succeeded, refused and broken into a single bar looked tidy and told us nothing: successes
 * outnumber failures by two orders of magnitude, so the two series that actually matter were slivers a
 * pixel tall. Splitting them gives the failures their own scale — a day with four errors is now clearly
 * four times a day with one — while the top chart keeps the volume they have to be read against. Two
 * panels, never two y-axes on one plot, which would let any shape be implied by choosing the scales.
 */
export default function HealthSection({ data, meta }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);

    const traffic = useMemo(() => {
        const buckets = buildBuckets(meta.logFrom, meta.to, meta.logUnit, data.series[0]?.at);
        return {
            labels: buckets.map((slot) => formatBucket(slot, meta.logUnit, lang)),
            total: alignToBuckets(data.series, buckets, meta.logUnit, (row) => row.total),
            rejected: alignToBuckets(data.series, buckets, meta.logUnit, (row) => row.rejected),
            errors: alignToBuckets(data.series, buckets, meta.logUnit, (row) => row.errors),
        };
    }, [data.series, meta.logFrom, meta.to, meta.logUnit, lang]);

    const count = (value: number) => formatNumber(value, lang);

    const hasProblems = data.errors > 0 || data.rejected > 0;

    return (
        <section className="home-section">
            <div className="home-section-head">
                <h2 className="home-section-title">{t.home.logsTitle}</h2>
                <NavLink className="home-section-link" to="/logs">
                    {t.home.viewAll}
                </NavLink>
            </div>

            {/* Said out loud rather than silently shortening the axis — a chart that quietly covers ten
                days while the filter says ninety reads as a collapse in traffic that never happened. */}
            {meta.logCapped && <p className="home-note">{t.home.logsCapped}</p>}

            <div className="home-tiles">
                <StatTile label={t.home.logsTotal} value={count(data.total)} tone="brand" to="/logs" />
                <StatTile
                    label={t.home.logsErrorRate}
                    value={formatPercent(data.errorRate, lang)}
                    tone={data.errors > 0 ? "bad" : "good"}
                    to="/logs"
                />
                <StatTile label={t.home.logsErrors} value={count(data.errors)} tone={data.errors > 0 ? "bad" : "neutral"} to="/logs" />
                <StatTile label={t.home.logsRejected} value={count(data.rejected)} tone="warn" to="/logs" />
                <StatTile label={t.home.logsAvgResponse} value={formatMs(data.avgResponseTime, lang)} />
            </div>

            <div className="home-card home-card--wide">
                <SeverityBar
                    segments={data.bySeverity.map((row) => ({
                        key: row.key,
                        label: t.enums.logSeverity[row.key as TyLogSeverity] ?? row.key,
                        value: row.value,
                        tone: TONES[row.key as TyLogSeverity] ?? "good",
                    }))}
                    emptyLabel={t.home.noData}
                />

                <div className="home-panel">
                    <p className="home-panel-title">{t.home.logsTraffic}</p>
                    <Chart
                        kind="area"
                        caption={t.home.logsTraffic}
                        labels={traffic.labels}
                        series={[{ label: t.home.logsTotal, data: traffic.total, color: "blue" }]}
                        emptyLabel={t.home.noData}
                    />
                </div>

                {/* Its own panel and its own scale, so a handful of failures is legible beside thousands
                    of successes. Hidden entirely when there are none — an empty panel reads as a bug. */}
                {hasProblems && (
                    <div className="home-panel">
                        <p className="home-panel-title">{t.home.logsProblems}</p>
                        <Chart
                            kind="stackedBar"
                            size="sm"
                            caption={t.home.logsProblems}
                            labels={traffic.labels}
                            series={[
                                { label: t.home.logsRejected, data: traffic.rejected, color: "warn" },
                                { label: t.home.logsErrors, data: traffic.errors, color: "bad" },
                            ]}
                            emptyLabel={t.home.noData}
                        />
                    </div>
                )}
            </div>
        </section>
    );
}
